import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  MDSTextVariant,
  MDSTextWeight,
  MDSText,
  MDSButton,
  MDSButtonVariant,
  MDSToggle,
} from "@/mds";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { StyledExplanation, StyledHeading } from "@/pages/integrations/shared/integrations.styles";
import { useAppStore } from "@/store";
import {
  MemCommonMentionKind,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
  syntaxTreeModule,
} from "@mem-labs/common-editor";
import { SearchSuggestion, SearchSuggestionType } from "@/domains/search";
import { NoteObservable } from "@/store/note/NoteObservable";
import { UNTITLED_COLLECTION_TITLE, UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { uuidModule } from "@/modules/uuid";
import { EventContext } from "@/domains/metrics/context";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import {
  InstructionsEditor,
  InstructionsEditorFetchDataHandler,
  InstructionsEditorMentionItem,
} from "@/pages/integrations/instructions-editor";
import { clientEnvModule } from "@/modules/client-env";

export const IntegrationsOrganizingForChromeExtension = observer(() => {
  const { store } = useAppStore();
  const isConnected = !!store.chromeExtension.isInstalled.data;
  const { isSaving, organizingInstructions, organizingInstructionsEnabled, save } =
    store.chromeExtension;

  const inputRef = useRef<MemCommonRichTextInputInstance>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  useLayoutEffect(() => {
    if (isResetting) setIsResetting(false);
  }, [isResetting]);

  const organizingInstructionsHtml = useMemo(() => {
    const htmlContent =
      organizingInstructions &&
      syntaxTreeModule.convertMdxToHtml({
        mdxContent: organizingInstructions,
      });
    return htmlContent;
  }, [organizingInstructions]);

  const [localOrganizingInstructionsHtml, setLocalOrganizingInstructionsHtml] = useState(
    organizingInstructionsHtml
  );

  const [localOrganizingInstructionsEnabled, setLocalOrganizingInstructionsEnabled] = useState(
    organizingInstructionsEnabled
  );

  const handleContentModified = useCallback(() => {
    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SendContents,
      payload: null,
    });
  }, []);

  const toggleSwitch = useCallback(() => {
    const enabled = !localOrganizingInstructionsEnabled;
    setLocalOrganizingInstructionsEnabled(enabled);
    setLocalOrganizingInstructionsHtml(enabled ? organizingInstructionsHtml : null);
    setIsEditing(enabled !== organizingInstructionsEnabled);
  }, [
    localOrganizingInstructionsEnabled,
    organizingInstructionsEnabled,
    organizingInstructionsHtml,
  ]);

  const handleSwitchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isSelected = event.target.checked;
      setLocalOrganizingInstructionsEnabled(isSelected);
      setLocalOrganizingInstructionsHtml(isSelected ? organizingInstructionsHtml : null);
      setIsEditing(isSelected !== organizingInstructionsEnabled);
    },
    [organizingInstructionsHtml, organizingInstructionsEnabled]
  );

  useEffect(() => {
    setLocalOrganizingInstructionsHtml(organizingInstructionsHtml);
    setLocalOrganizingInstructionsEnabled(organizingInstructionsEnabled);
  }, [organizingInstructionsHtml, organizingInstructionsEnabled]);

  const handleFetchData = useCallback<
    InstructionsEditorFetchDataHandler<InstructionsEditorMentionItem>
  >(
    async ({ query, mentionSymbol }: { query: string; mentionSymbol?: string }) => {
      const suggestionType =
        mentionSymbol === "@"
          ? SearchSuggestionType.NOTE
          : mentionSymbol === "#"
            ? SearchSuggestionType.COLLECTION
            : undefined;
      if (!suggestionType) {
        return {
          data: [],
          isLoading: false,
        };
      }
      if (!query) {
        return {
          data: (suggestionType === SearchSuggestionType.NOTE
            ? store.recentItems.sortedRecentNotesInteractedWithByMe
            : store.recentItems.sortedRecentCollectionsInteractedWithByMe
          )
            .filter(item => item.isAvailable)
            .slice(0, 5)
            .map(item => {
              return {
                id: item.id,
                label:
                  item.title ??
                  (item instanceof NoteObservable
                    ? UNTITLED_NOTE_TITLE
                    : UNTITLED_COLLECTION_TITLE),
                kind:
                  item instanceof NoteObservable
                    ? MemCommonMentionKind.Note
                    : MemCommonMentionKind.Collection,
              };
            }),
          isLoading: false,
        };
      }
      const condition = (item: SearchSuggestion) => item.type === suggestionType;
      const suggestions = await store.search.forSuggestions(query, "mentions", condition, 5);
      if (!suggestions.length && suggestionType === SearchSuggestionType.COLLECTION) {
        const collectionId = uuidModule.generate();
        return {
          data: [
            {
              id: collectionId,
              label: "Create new collection",
              kind: "create-new-collection",
              onClick: async () => {
                const title = query;
                await store.collections.createCollection({
                  collectionId,
                  title,
                  eventContext: EventContext.EditorInline,
                });
                return title;
              },
            },
          ],
          isLoading: false,
        };
      }
      return {
        data: suggestions.map(suggestion => ({
          id: suggestion.modelId,
          label: suggestion.label,
          kind:
            suggestion.type === SearchSuggestionType.NOTE
              ? MemCommonMentionKind.Note
              : MemCommonMentionKind.Collection,
        })),
        isLoading: false,
      };
    },
    [store.search, store.recentItems, store.collections]
  );

  const handleComboboxSubmit = useCallback((payload: { htmlContent: string }) => {
    setLocalOrganizingInstructionsHtml(payload.htmlContent);
  }, []);

  const handleExtraContentClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(() => {
    if (isEditing) return;

    setIsEditing(true);
    return true;
  }, [isEditing]);

  const handleCancelClick = useCallback(() => {
    setLocalOrganizingInstructionsHtml(organizingInstructionsHtml);
    setLocalOrganizingInstructionsEnabled(organizingInstructionsEnabled);
    setIsEditing(false);
    setIsResetting(true);
  }, [organizingInstructionsHtml, organizingInstructionsEnabled]);

  const handleSaveClick = useCallback(async () => {
    const mdxContent = syntaxTreeModule.convertHtmlToMdx({
      htmlContent: localOrganizingInstructionsHtml ?? "",
    });
    await save({
      organizingInstructions: mdxContent,
      organizingInstructionsEnabled: localOrganizingInstructionsEnabled,
    });

    trackEvent(TrackedEvent.ChromeExtensionCustomInstructionsSave, {
      custom_instructions: mdxContent,
    });

    setIsResetting(true);
    setIsEditing(false);
  }, [localOrganizingInstructionsEnabled, localOrganizingInstructionsHtml, save]);

  if (!isConnected && !clientEnvModule.isDesktop()) return null;

  return (
    <>
      <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
        Smart Handling
      </StyledHeading>
      <StyledExplanation variant={MDSTextVariant.ParagraphS}>
        Mem automatically creates, updates, and organizes formatted notes based on the webpages you
        save
      </StyledExplanation>
      <StyledOrganizingInstructions isEditing={isEditing}>
        <SwitchContainer onClick={toggleSwitch}>
          <StyledToggle
            checked={localOrganizingInstructionsEnabled}
            onChange={handleSwitchChange}
          />
          <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
            Customize how Mem handles webpages you save
          </MDSText>
        </SwitchContainer>
        {localOrganizingInstructionsEnabled && !isResetting && (
          <ExtraContentContainer>
            <ExtraContent onClickCapture={handleExtraContentClick}>
              <InstructionsEditor
                isDisabled={!isEditing}
                inputRef={inputRef}
                setContent={localOrganizingInstructionsHtml ?? EMPTY_HTML}
                editorDimensions={{
                  minHeight: 136,
                  maxHeight: 450,
                }}
                placeholder={`For all news articles, creates notes with the article title, one-line tl;dr, and summary of key points, and add them to my #ToRead collection.

For all recipes, create notes with just the recipe title, list of ingredients, and numbered list of instructions, and add them to my #MyRecipes collection.`}
                fetchData={handleFetchData}
                onContentModified={handleContentModified}
                onSubmit={handleComboboxSubmit}
              />
            </ExtraContent>
          </ExtraContentContainer>
        )}
        {isEditing && (
          <LeftStyles>
            <MDSButton onClick={handleCancelClick}>Cancel</MDSButton>
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              onClick={handleSaveClick}
              isLoading={isSaving}
              isDisabled={isSaving}
            >
              Save Settings
            </MDSButton>
          </LeftStyles>
        )}
      </StyledOrganizingInstructions>
    </>
  );
});

const SwitchContainer = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  height: 40px;
  user-select: none;
`;

const StyledToggle = styled(MDSToggle)``;

const StyledOrganizingInstructions = styled.div<{ isEditing: boolean }>`
  ${({ theme, isEditing }) => isEditing && `background-color: ${theme.colors.grey.x25};`}
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.surface.pane};
  padding: ${({ theme }) => `${theme.spacing[4]} ${theme.spacing[3]}`};
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 784px;
`;

const ExtraContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
  padding-left: 52px;
  margin-top: -4px;
`;

const ExtraContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  position: relative;
  transform-origin: top;
  transition: transform 0.2s ease-in-out;
`;

const LeftStyles = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const EMPTY_HTML = "<html><body></body></html>";
